<template>
  <div class="hcl-tiles-editor-layout" ref="editorLayoutRef" :class="classInteraction" :style="stylingTilesDisplay">
    <div class="grid-wrapper">
      <template v-for="r in props.tiles.dimensions.rows.max">
        <template v-for="c in props.tiles.dimensions.cols.max" :key="r + '-' + c">
          <div class="grid" :style="stylingTileCalc(r - 1, c - 1, 1, 1)">
            {{ r - 1 }}, {{ c - 1 }}
          </div>
        </template>
      </template>
    </div>
    <hcl-tiles-tile v-for="tile in tilesOnForm" :key="tile.id" :tile="tile" :dimensions="props.tiles.dimensions"
      :render="false" @resize="startResize" @drag="startPosition" v-on:tile-chooser-change="onTileChooserChange" />
    <div v-show="!placeholder.hidden" class="placeholder" :class="{ invalid: placeholder.invalid }"
      :style="stylingPlaceholder"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'

import HclTilesTile from '@/components/TilesTile/TilesTile.vue'

import { DefinitionTilePlaceholder, DefinitionTileInteraction, DefinitionTile } from '@/components/types'

import { useUtils } from '@/components/TilesEditor/utils'

interface Props {
  /**
   * Definition as an JS object.
   */
  tiles: any
  dragging: any
  dimensions: any
}

const props = withDefaults(defineProps<Props>(), {
  tiles: undefined,
  dragging: undefined,
  dimensions: undefined
})

const editorLayoutRef = ref()

const placeholder: DefinitionTilePlaceholder = reactive({
  tile: {
    id: 0,
    row: 0,
    column: 0,
    height: 0,
    width: 0,
    chooser: false,
    packing: -1,
    entity: { type: '', name: '' },
    text: ''
  },
  interaction: 'resize',
  hidden: true,
  invalid: false,
  top: 0, // top in px
  left: 0, // left in px
  height: 1, // height in px
  width: 1 // width in px
})

const interaction: DefinitionTileInteraction = reactive({
  tile: {
    id: 0,
    row: 0,
    column: 0,
    height: 0,
    width: 0,
    chooser: false,
    packing: -1,
    entity: { type: '', name: '' },
    text: ''
  },
  handler: () => {
    return
  },
  row0: 0, // row position in row units
  column0: 0, // col position in col units
  y0: 0, // row position in px
  x0: 0, // col position in px
  t0: 0, // initial top in px
  l0: 0, // initial left in px
  h0: 0, // initial height in px
  w0: 0, // initial width in px
  yu: 0, // height of row in px
  xu: 0, // width of column in px
  grid: [[]],
  dragging: false
})

interface EventTileChooserChange {
  id: string | number
  chooser: boolean
}

const onTileChooserChange = (event: EventTileChooserChange) => {
  const f = props.tiles.tiles.filter((tile: DefinitionTile) => {
    return tile.id === event.id
  })

  if (f.length > 0) f[0].chooser = event.chooser
}

const { stylingTilesDisplay, stylingTileCalc, tilesOnForm } = useUtils(props)

const stylingPlaceholder = computed(() => {
  return {
    top: placeholder.top + 'px',
    left: placeholder.left + 'px',
    height: placeholder.height + 'px',
    width: placeholder.width + 'px'
  }
})

const classInteraction = computed(() => {
  if (placeholder.hidden) return ''
  return 'interaction ' + placeholder.interaction
})

const createValidationGrid = (ref) => {
  const rows = props.tiles.dimensions.rows.max
  const columns = props.tiles.dimensions.cols.max

  interaction.grid = new Array(rows)
  for (let row = 0; row < rows; row++) {
    const a = (interaction.grid[row] = new Array(columns))
    for (let column = 0; column < columns; column++) a[column] = 1
  }

  for (let index = 0; index < props.tiles.tiles.length; index++) {
    const tile = props.tiles.tiles[index]

    if (tile === ref || tile.chooser) continue

    for (let r = 0; r < tile.h; r++) {
      for (let c = 0; c < tile.w; c++) {
        interaction.grid[tile.y + r][tile.x + c] = 0
      }
    }
  }
}

const unitsInteractionCalc = () => {
  if (!document.defaultView) {
    return
  }

  const elcs = document.defaultView.getComputedStyle(editorLayoutRef.value)

  interaction.yu = parseInt(elcs.height) / props.tiles.dimensions.rows.max
  interaction.xu = parseInt(elcs.width) / props.tiles.dimensions.cols.current
}

const cleanupInteraction = () => {
  placeholder.hidden = true
  interaction.tile = {
    id: 0,
    row: 0,
    column: 0,
    height: 0,
    width: 0,
    chooser: false,
    packing: -1,
    entity: { type: '', name: '' }
  }
  interaction.grid = [[]]
}

const stopInteraction = () => {
  document.documentElement.removeEventListener('mousemove', interaction.handler, false)
  document.documentElement.removeEventListener('mouseup', stopInteraction, false)

  cleanupInteraction()
}

const startInteraction = (e, type, handler) => {
  unitsInteractionCalc()

  placeholder.hidden = false
  placeholder.invalid = false
  placeholder.interaction = type

  placeholder.top = interaction.t0 = e.initialPos.top
  placeholder.left = interaction.l0 = e.initialPos.left
  placeholder.width = interaction.w0 = e.initialPos.width
  placeholder.height = interaction.h0 = e.initialPos.height

  interaction.x0 = e.event.clientX
  interaction.y0 = e.event.clientY
  interaction.tile = e.tile
  interaction.handler = handler

  createValidationGrid(e.tile)

  document.documentElement.addEventListener('mousemove', interaction.handler, false)
  document.documentElement.addEventListener('mouseup', stopInteraction, false)
}

const validateTile = (row: number, column: number, height: number, width: number) => {
  for (let j = 0; j < height; j++) {
    for (let k = 0; k < width; k++) {
      const r = interaction.grid[row + j]
      if (!r || !r[column + k]) return false
    }
  }
  return true
}

const updateTile = (row: number, column: number, height: number, width: number) => {
  const tile = interaction.tile
  if (
    tile.row == row &&
    tile.column == column &&
    tile.height == height &&
    tile.width == width
  ) {
    placeholder.invalid = false
    return
  }

  if (validateTile(row, column, height, width)) {
    tile.row = row
    tile.column = column
    tile.height = height
    tile.width = width
    placeholder.invalid = false
  } else {
    placeholder.invalid = true
  }
}

const movePosition = (e) => {
  placeholder.top = interaction.t0 + e.clientY - interaction.y0
  placeholder.left = interaction.l0 + e.clientX - interaction.x0

  updateTile(
    Math.round(placeholder.top / interaction.yu),
    Math.round(placeholder.left / interaction.xu),
    interaction.tile.height,
    interaction.tile.width
  )
}

const startPosition = (e) => {
  startInteraction(e, 'drag', movePosition)
}

const resizeResize = (e) => {
  placeholder.height = interaction.h0 + e.clientY - interaction.y0
  placeholder.width = interaction.w0 + e.clientX - interaction.x0

  updateTile(
    interaction.tile.row,
    interaction.tile.column,
    Math.max(1, Math.round(placeholder.height / interaction.yu)),
    Math.max(1, Math.round(placeholder.width / interaction.xu))
  )
}

const startResize = (e) => {
  startInteraction(e, 'resize', resizeResize)
}

/*
  * Drag a tile from the chooser to the layout, tile is already in layout
  */
const overDrag = (e) => {
  // editor layout DOM dimensions...
  const bounds = editorLayoutRef.value.getBoundingClientRect()

  // calculate (new) position in row and col units
  const y = Math.floor((bounds.height - (bounds.bottom - e.clientY)) / interaction.yu)
  const x = Math.floor((bounds.width - (bounds.right - e.clientX)) / interaction.xu)

  if (y == interaction.row0 && x == interaction.column0) return

  interaction.row0 = y
  interaction.column0 = x

  placeholder.top = y * interaction.yu
  placeholder.left = x * interaction.xu

  placeholder.invalid = !validateTile(y, x, placeholder.tile.height, placeholder.tile.width)
}

/*
  * Drag a tile from the chooser to the layout, tile enters layout...
  */
const enterDrag = (e) => {
  if (!props.dragging.active) return

  interaction.dragging = true
  interaction.row0 = props.tiles.dimensions.rows.current
  interaction.column0 = -1

  unitsInteractionCalc()

  placeholder.hidden = false
  placeholder.interaction = 'dnd'
  placeholder.invalid = false

  const tile = props.tiles.tiles[props.dragging.index]

  placeholder.tile = tile

  placeholder.height = tile.height * interaction.yu
  placeholder.width = tile.width * interaction.xu

  createValidationGrid(null)

  overDrag(e)
}

const leaveDrag = () => {
  placeholder.hidden = true
  interaction.dragging = false
}

const endDrag = () => {
  editorLayoutRef.value.removeEventListener('mouseenter', enterDrag, false)
  editorLayoutRef.value.removeEventListener('mouseleave', leaveDrag, false)
  editorLayoutRef.value.removeEventListener('mousemove', overDrag, false)

  cleanupInteraction()

  if (!interaction.dragging || props.dragging.cancelled || placeholder.invalid) return

  const tile = props.tiles.tiles[props.dragging.index]
  if (tile && tile.chooser) {
    tile.chooser = false
    tile.row = interaction.row0
    tile.column = interaction.column0
  }
}

const startDrag = () => {
  interaction.dragging = false
  editorLayoutRef.value.addEventListener('mouseenter', enterDrag, false)
  editorLayoutRef.value.addEventListener('mouseleave', leaveDrag, false)
  editorLayoutRef.value.addEventListener('mousemove', overDrag, false)
}

watch(props.dragging, (newval) => {
  if (newval.active) startDrag()
  else endDrag()
})
</script>

<style lang="scss">
.hcl-tiles-editor-layout {
  height: 600px;
  flex: 1 1 auto;
  position: relative;
  margin: 0;
  padding: 0;
  overflow: hidden;
  border: 1px solid #ccc;

  &,
  * {
    box-sizing: border-box;
  }

  .grid {
    position: absolute;
    z-index: 0;
    border-style: solid;
    border-color: #eee;
    border-width: 0 1px 1px 0;
    color: #666;
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;

    &.right {
      border-right-width: 0;
    }

    &.bottom {
      border-bottom-width: 0;
    }
  }

  .placeholder {
    position: absolute;
    z-index: 3;
    border: 1px solid #ddd;
    background-color: rgba(160, 160, 160, 0.1);
    transition: background-color 0.6s;

    &.invalid {
      border-color: #ffa7a7 !important;
      background-color: rgba(255, 0, 0, 0.1) !important;
    }
  }

  &.interaction {
    &.resize {
      cursor: nwse-resize;
    }

    &.drag {
      cursor: move;
    }

    &.dnd {
      .placeholder {
        border-color: transparent;
        background-color: rgba(0, 255, 0, 0.1);
        transition-property: top, left, width, height, background-color;
        transition-duration: 0.4s, 0.4s, 0.4s, 0.4s, 0.6s;
      }
    }
  }
}
</style>
